import React, { ReactElement } from "react";
//#region types
import { RevenueReportingFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { DialogEnums, DimensionEnums, StyleEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
import { KeyValue } from "models/utilTypes/keyValue";
import { TagsSectionItem, TagsSectionProps, TagsSectionValues } from "models/propTypes/dimensionPropTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { DetailsTableViewSmall, DialogEvents, StyledButton, TitleSmall } from "components/common";
import ContentDialog from "components/common/dialogs/_contentDialog";
import useDimensionMaps from "components/common/dimensions/useDimensionMaps";
import TagsForm from "./tagsForm";
import { useFormikContext } from "formik";
//#endregion
import { DimensionHelper, LocalizationHelperInstance } from "utils/helpers";
import {
  applyRowDimensions,
  applyWholeReportDimensions,
  getWholeReportDimensions,
  hasAnyTags,
  toTagRows,
  toWholeReportRows,
} from "./tagsHelper";
import cssExports from "theme/styles/main.module.scss";

type ReadBackRow = KeyValue<string | undefined, string | ReactElement>;

const TagsSection = <TItem extends TagsSectionItem, TValues extends TagsSectionValues<TItem>>({
  getRowId,
  otherHeadingKey,
  translateDimensionLabels,
}: TagsSectionProps<TItem>): ReactElement => {
  const { values, setValues } = useFormikContext<TValues>();
  const { dimensions } = useDimensionMaps(DimensionEnums.DimensionCategory.CashReconciliation);

  const allDimensions = React.useMemo(() => dimensions.flatMap((dimension) => dimension.value), [dimensions]);

  const getDimensionNames = (dimensionIds?: string[]) =>
    (dimensionIds ?? [])
      .map((dimensionId) => allDimensions.find((dimension) => dimension.id === dimensionId)?.name)
      .filter((name) => !!name) as string[];

  const getValueContent = (names: string[]) => (
    <Grid container className={cssExports.revenueReportingTagValues}>
      {names.map((name, index) => (
        <Grid item key={`${name}-${index}`}>
          {name}
        </Grid>
      ))}
    </Grid>
  );

  const getWholeReportReadBackRows = (): ReadBackRow[] =>
    dimensions
      .map((dimension) => ({
        label: DimensionHelper.getDimensionTypeText(dimension.key.type, dimension.key.name),
        names: getDimensionNames(
          dimension.value
            .filter((item) => item.id && (values.dimensions ?? []).includes(item.id))
            .map((item) => item.id as string)
        ),
      }))
      .filter((dimension) => dimension.names.length > 0)
      .map(
        (dimension) =>
          new KeyValue<string | undefined, string | ReactElement>(dimension.label, getValueContent(dimension.names))
      );

  const getItemReadBackRows = (items?: TItem[]): ReadBackRow[] =>
    (items ?? [])
      .map((item) => ({ description: item.description, names: getDimensionNames(item.dimensions) }))
      .filter((item) => item.names.length > 0)
      .map(
        (item) => new KeyValue<string | undefined, string | ReactElement>(item.description, getValueContent(item.names))
      );

  const getReadBackGroups = (): { headingKey?: string; rows: ReadBackRow[] }[] => {
    if (!values.hasRowLevelDimensions) {
      const wholeReportRows = getWholeReportReadBackRows();
      return wholeReportRows.length ? [{ rows: wholeReportRows }] : [];
    }

    return [
      { headingKey: TranslationKeys.Common.Sale, rows: getItemReadBackRows(values.salesItems) },
      { headingKey: TranslationKeys.Common.PaymentMethod, rows: getItemReadBackRows(values.paymentItems) },
      { headingKey: otherHeadingKey, rows: getItemReadBackRows(values.otherItems) },
    ].filter((group) => group.rows.length > 0);
  };

  const getTagsFormValues = (): RevenueReportingFormTypes.EditTags => ({
    editInRows: values.hasRowLevelDimensions,
    reportRows: toWholeReportRows(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.WholeReport),
      values.dimensions
    ),
    salesRows: toTagRows(getRowId, values.salesItems),
    paymentRows: toTagRows(getRowId, values.paymentItems),
    otherRows: toTagRows(getRowId, values.otherItems),
  });

  const onTagsCommit = async (tagValues: RevenueReportingFormTypes.EditTags) => {
    const editInRows = !!tagValues.editInRows;
    const wholeReportDimensions = editInRows ? [] : getWholeReportDimensions(tagValues.reportRows);

    const applyToItems = (items?: TItem[], rows?: RevenueReportingFormTypes.EditTagsRow[]) =>
      editInRows ? applyRowDimensions(getRowId, items, rows) : applyWholeReportDimensions(items, wholeReportDimensions);

    setValues({
      ...values,
      hasRowLevelDimensions: editInRows,
      dimensions: wholeReportDimensions,
      salesItems: applyToItems(values.salesItems, tagValues.salesRows),
      paymentItems: applyToItems(values.paymentItems, tagValues.paymentRows),
      otherItems: applyToItems(values.otherItems, tagValues.otherRows),
    });
  };

  const readBackGroups = getReadBackGroups();
  const hasTags = hasAnyTags(values);

  return (
    <DialogEvents
      render={(isOpen, openDialog, closeDialog, key) => (
        <Grid container direction="column" className={cssExports.revenueReportingTags}>
          {readBackGroups.map((group, index) => (
            <Grid item container direction="column" key={`revenueReportingTagGroup-${index}`}>
              {group.headingKey ? <TitleSmall title={group.headingKey} /> : <></>}
              <DetailsTableViewSmall detailsList={group.rows} hideButton />
            </Grid>
          ))}
          <Grid item>
            <StyledButton size="sm" displayType={StyleEnums.DisplayType.Info} onClick={openDialog}>
              {LocalizationHelperInstance.getFormattedMessage(
                hasTags ? TranslationKeys.RevenueReporting.ChangeTags : TranslationKeys.RevenueReporting.AddTags
              )}
            </StyledButton>
          </Grid>
          <ContentDialog
            key={key}
            isOpen={isOpen}
            dialogType={DialogEnums.DialogType.Content}
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.EditTags)}
            titleIcon={IconDefinitionConstants.WHashtag}
            maxWidth="md"
            fullWidth
            preventClickOutsideClose
            closeDialog={closeDialog}
            actions={[{ text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Done) }]}
            children={
              <TagsForm
                defaultValues={getTagsFormValues()}
                otherHeadingKey={otherHeadingKey}
                translateDimensionLabels={translateDimensionLabels}
                handleSubmit={onTagsCommit}
              />
            }
          />
        </Grid>
      )}
    />
  );
};

export default TagsSection;
